import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AritoIcon } from '@/components/custom/erp';
import { FormMode } from '@/types/form';

interface ButtonConfig {
  type: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  icon?: number;
  showTitle?: boolean;
}

interface DetailTableActionBarProps {
  mode: FormMode;
  handleAdd?: () => void;
  handleDelete?: () => void;
  handleCopy?: () => void;
  handleEdit?: () => void;
  handleView?: () => void;
  handleRefresh?: () => void;
  handleDownloadTemplate?: () => void;
  handleImportExcel?: () => void;
}
const buttonConfigs = {
  add: { icon: 7, title: 'Thêm mới', showTitle: true },
  edit: { icon: 864, title: 'Sửa', showTitle: true },
  delete: { icon: 8, title: 'Xóa', showTitle: true },
  copy: { icon: 11, title: 'Sao chép', showTitle: true },
  refresh: { icon: 15, title: 'Làm mới', showTitle: true },
  watch: { icon: 19, title: 'Xem', showTitle: true },
  downloadTemplate: { icon: 28, title: 'Tải mẫu excel', showTitle: true },
  importExcel: { icon: 29, title: 'Nhập từ excel', showTitle: true }
};

const renderActionButton = (button: ButtonConfig): React.ReactNode => {
  const isDisabled = button.disabled || false;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='flex cursor-default items-center hover:bg-blue-100 disabled:text-black/25'
            onClick={button.onClick}
            disabled={isDisabled}
          >
            <span className='flex size-10 min-w-10 items-center justify-center'>
              <AritoIcon icon={button.icon || 0} />
            </span>
            {button.showTitle && <span className='pr-1 text-sm font-medium text-gray-700'>{button.title}</span>}
          </button>
        </TooltipTrigger>
        <TooltipContent>{button.title}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default function DetailTableActionBar({
  mode,
  handleAdd,
  handleDelete,
  handleCopy,
  handleEdit,
  handleView,
  handleRefresh,
  handleDownloadTemplate,
  handleImportExcel
}: DetailTableActionBarProps) {
  const buttonTypes: (keyof typeof buttonConfigs)[] = [
    'add',
    'edit',
    'delete',
    'copy',
    'watch',
    'refresh',
    'downloadTemplate',
    'importExcel'
  ];

  const buttonActions = {
    add: () => handleAdd?.(),
    edit: () => handleEdit?.(),
    delete: () => handleDelete?.(),
    watch: () => handleView?.(),
    copy: () => handleCopy?.(),
    refresh: () => handleRefresh?.(),
    downloadTemplate: () => handleDownloadTemplate?.(),
    importExcel: () => handleImportExcel?.()
  } as Record<keyof typeof buttonConfigs, () => void>;

  const buttons: ButtonConfig[] = buttonTypes.map(type => ({
    type,
    onClick: buttonActions[type],
    disabled: type === 'downloadTemplate' || type === 'importExcel' ? false : mode === 'view',
    ...buttonConfigs[type]
  }));

  return (
    <div className='flex h-9 flex-none items-center justify-between border-b border-gray-200 bg-white px-1 py-0'>
      <div className='flex h-full flex-1 items-center justify-between'>
        <div className='flex items-center gap-1'>
          {buttons.map((button, index) => (
            <span key={button.type + index}>{renderActionButton(button)}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
